import type { FulfillmentOrderLine, FulfillmentOrderLineAdjustment } from "../types";
import { normalizeFulfillmentResourceType } from "../types";

export type StructuredFeeAdjustmentValue = FulfillmentOrderLineAdjustment;

/** 原录入没有规格乘数的类型只读展示，不重置历史值。 */
export function canEditFeeAdjustmentBillingCount(resourceType: string): boolean {
  return !["大交通", "包车", "杂费"].includes(normalizeFulfillmentResourceType(resourceType));
}

export type ChangeDirection = "increase" | "decrease" | "same";

const DECIMAL_PATTERN = /^(?:0|[1-9]\d*)(?:\.(\d{1,2}))?$/u;

/** 把最多两位小数的非负文本转换为百分之一单位。 */
function decimalUnits(value: string): bigint | null {
  const normalized = value.trim();
  const match = DECIMAL_PATTERN.exec(normalized);
  if (!match) return null;
  const [whole = "0", fraction = ""] = normalized.split(".");
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0") || "0");
}

/** 把百分之一单位格式化成固定两位小数字符串。 */
export function formatDecimalUnits(value: bigint): string {
  const whole = value / 100n;
  const fraction = (value % 100n).toString().padStart(2, "0");
  return `${whole}.${fraction}`;
}

/** 返回六类确认费用的固定规格单位。 */
export function feeAdjustmentSpecUnit(resourceType: string): "晚" | "次" | "人" {
  if (["住宿", "stay"].includes(resourceType)) return "晚";
  if (["导游", "guide"].includes(resourceType)) return "人";
  return "次";
}

/** 比较两个合法十进制文本；非法输入由调用方先拦截。 */
export function compareDecimalText(before: string, after: string): ChangeDirection {
  const beforeUnits = decimalUnits(before);
  const afterUnits = decimalUnits(after);
  if (beforeUnits === null || afterUnits === null || beforeUnits === afterUnits) return "same";
  return afterUnits > beforeUnits ? "increase" : "decrease";
}

export type StructuredFeeAdjustmentPreview = {
  normalized: StructuredFeeAdjustmentValue | null;
  amount: string;
  amountDirection: ChangeDirection;
  changed: boolean;
  error: string | null;
};

/** 使用整数小数位实时复算新总额，舍入规则与后端 Decimal ROUND_HALF_UP 一致。 */
export function previewStructuredFeeAdjustment(
  line: Pick<FulfillmentOrderLine, "quantity" | "billingCount" | "unitPriceAmount" | "amount">,
  value: StructuredFeeAdjustmentValue,
): StructuredFeeAdjustmentPreview {
  const quantity = decimalUnits(value.quantity);
  const billingCount = decimalUnits(value.billingCount);
  const unitPrice = decimalUnits(value.unitPriceAmount);
  if (quantity === null) {
    return { normalized: null, amount: line.amount, amountDirection: "same", changed: false, error: "数量必须为最多两位小数的非负数" };
  }
  if (billingCount === null || billingCount <= 0n) {
    return { normalized: null, amount: line.amount, amountDirection: "same", changed: false, error: "规格数必须为最多两位小数的正数" };
  }
  if (unitPrice === null) {
    return { normalized: null, amount: line.amount, amountDirection: "same", changed: false, error: "单价必须为最多两位小数的非负数" };
  }
  const amountUnits = (quantity * billingCount * unitPrice + 5_000n) / 10_000n;
  const amount = formatDecimalUnits(amountUnits);
  const normalized = {
    quantity: formatDecimalUnits(quantity),
    billingCount: formatDecimalUnits(billingCount),
    specUnit: value.specUnit,
    unitPriceAmount: formatDecimalUnits(unitPrice),
  };
  const changed = normalized.quantity !== Number(line.quantity).toFixed(2)
    || normalized.billingCount !== Number(line.billingCount).toFixed(2)
    || normalized.unitPriceAmount !== Number(line.unitPriceAmount).toFixed(2);
  return {
    normalized,
    amount,
    amountDirection: compareDecimalText(line.amount, amount),
    changed,
    error: changed ? null : "数量、规格数和单价均未发生变化",
  };
}
